import React, { useEffect, useState, useMemo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { MetadataImportModal } from '../components/MetadataImportModal';
import { CampaignIntelligenceTab } from '../components/campaign-intelligence/CampaignIntelligenceTab';
import { analyticsApi, facebookApi } from '../lib/api';
import { CreativeUcode, CreativeDailyMetric } from '../types';
import { formatNumber } from '../lib/utils';
import {
  TrendingUp, AlertTriangle, RefreshCw, DollarSign, Eye, MousePointer, Facebook,
  LogOut, CheckCircle, XCircle, ChevronDown, ChevronRight, Download, Search, Filter
} from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

// Facebook App ID from environment
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '735375959485927';

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

type TabType = 'overview' | 'editor-rankings' | 'creatives' | 'hit-rate' | 'all-data' | 'campaign-intelligence';

const BUDGET_COLORS = ['#60a5fa', '#4ade80', '#c084fc', '#fbbf24', '#f87171', '#2dd4bf'];

function roasColor(roas: number): string {
  if (roas >= 1.2) return 'text-green-600 dark:text-green-400';
  if (roas >= 1.0) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function getAgeTagColor(ageTag?: string): string {
  switch (ageTag) {
    case 'in_learning': return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
    case 'active': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
    case 'mature': return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
    default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  }
}

function formatMetric(value: number | null | undefined, format: 'currency' | 'percent' | 'number' = 'number'): string {
  if (value === null || value === undefined) return '—'; // Per SOP: NULL vs $0 distinction
  if (format === 'currency') return `$${Math.round(value).toLocaleString()}`;
  if (format === 'percent') return `${value.toFixed(2)}%`;
  return value.toFixed(2);
}

function formatCurrency(num: number): string {
  return `$${Math.round(num).toLocaleString()}`;
}

export function AnalyticsPage() {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Filter state
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedVertical, setSelectedVertical] = useState('');
  const [filterLoading, setFilterLoading] = useState(false);

  // Creative analytics state
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [ucodes, setUcodes] = useState<CreativeUcode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Expandable state for creatives tab
  const [expandedUcodes, setExpandedUcodes] = useState<Set<string>>(new Set());
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [ucodeDetails, setUcodeDetails] = useState<Map<string, any>>(new Map());
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);

  // Search and filter for all-data tab
  const [searchTerm, setSearchTerm] = useState('');
  const [creativeTypeFilter, setCreativeTypeFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('spend');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Facebook sync state
  const [syncing, setSyncing] = useState(false);
  const [fbConnected, setFbConnected] = useState(false);
  const [showFacebookSection, setShowFacebookSection] = useState(true);

  // Metadata import state
  const [showMetadataImportModal, setShowMetadataImportModal] = useState(false);
  const [syncDateFrom, setSyncDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [syncDateTo, setSyncDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Derived data
  const verticals = useMemo(() => {
    const v = new Set(ucodes.map(u => u.vertical).filter(Boolean));
    return Array.from(v).sort();
  }, [ucodes]);

  const filteredUcodes = useMemo(() => {
    return ucodes.filter(u => {
      if (selectedVertical && u.vertical !== selectedVertical) return false;
      if (creativeTypeFilter && u.creative_type !== creativeTypeFilter) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return u.ucode.toLowerCase().includes(q) || u.editor_name?.toLowerCase().includes(q);
      }
      return true;
    });
  }, [ucodes, selectedVertical, creativeTypeFilter, searchTerm]);

  const sortedUcodes = useMemo(() => {
    const sorted = [...filteredUcodes];
    sorted.sort((a, b) => {
      let aVal: any = a[sortColumn as keyof CreativeUcode];
      let bVal: any = b[sortColumn as keyof CreativeUcode];
      if (aVal === undefined) aVal = 0;
      if (bVal === undefined) bVal = 0;
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      return sortDirection === 'desc' ? (bVal > aVal ? 1 : -1) : (aVal > bVal ? 1 : -1);
    });
    return sorted;
  }, [filteredUcodes, sortColumn, sortDirection]);

  // Initialize Facebook SDK
  const initializeFacebookSDK = () => {
    window.fbAsyncInit = function() {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });
    };

    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  };

  const checkFacebookStatus = () => {
    if (window.FB) {
      window.FB.getLoginStatus((response: any) => {
        setFbConnected(response.status === 'connected');
      });
    }
  };

  // Fetch creative analytics data
  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        date_from: dateFrom,
        date_to: dateTo,
        ...(selectedVertical && { vertical: selectedVertical })
      };

      const [perfResp, ucodesResp] = await Promise.all([
        analyticsApi.getCreativePerformance(params),
        analyticsApi.getCreativeUcodes(params)
      ]);

      setPerformanceData(perfResp.data?.data);
      setUcodes(ucodesResp.data?.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch analytics data');
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = async () => {
    setFilterLoading(true);
    try {
      await fetchData();
    } finally {
      setFilterLoading(false);
    }
  };

  const toggleUcodeExpand = async (ucode: string) => {
    const newSet = new Set(expandedUcodes);
    if (newSet.has(ucode)) {
      newSet.delete(ucode);
    } else {
      // Fetch detail on first expand
      if (!ucodeDetails.has(ucode)) {
        setLoadingDetail(ucode);
        try {
          const resp = await analyticsApi.getCreativeUcodeDetail(ucode);
          const newMap = new Map(ucodeDetails);
          newMap.set(ucode, resp.data?.data);
          setUcodeDetails(newMap);
        } catch (err) {
          console.error('Failed to fetch UCODE detail:', err);
        } finally {
          setLoadingDetail(null);
        }
      }
      newSet.add(ucode);
    }
    setExpandedUcodes(newSet);
  };

  const toggleAccountExpand = (accountId: string) => {
    const newSet = new Set(expandedAccounts);
    if (newSet.has(accountId)) {
      newSet.delete(accountId);
    } else {
      newSet.add(accountId);
    }
    setExpandedAccounts(newSet);
  };

  const syncAdMappings = async () => {
    setSyncing(true);
    try {
      const resp = await analyticsApi.syncCreativeMappings();
      alert(`Sync complete: ${resp.data?.data?.matched || 0} ads matched`);
      await fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const syncRedTrackData = async () => {
    setSyncing(true);
    try {
      const resp = await analyticsApi.syncRedTrackData();
      alert(`RedTrack sync initiated: ${resp.data?.data?.queued_count || 0} UCODEs queued`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'RedTrack sync failed');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    initializeFacebookSDK();
    checkFacebookStatus();
    fetchData();
  }, []);

  // Calculate hit rate definition
  const hitUcodes = useMemo(() => {
    return ucodes.filter(u => (u.hook_rate || 0) > 45 && (u.hold_rate || 0) > 30);
  }, [ucodes]);

  const nearMissUcodes = useMemo(() => {
    return ucodes.filter(u =>
      (u.hook_rate || 0) > 38 && (u.hold_rate || 0) > 25 &&
      ((u.spend || 0) > 30) &&
      !hitUcodes.some(hit => hit.ucode === u.ucode)
    );
  }, [ucodes, hitUcodes]);

  // Render functions
  const renderOverviewTab = () => {
    if (!performanceData) return <div className="p-4">No data available</div>;

    const kpis = performanceData.kpis || {};
    const editorRankings = performanceData.editor_rankings || [];
    const budgetShare = performanceData.budget_share || [];

    const topEditorByRoas = editorRankings.length > 0
      ? editorRankings.sort((a: any, b: any) => (b.roas || 0) - (a.roas || 0))[0]
      : null;

    return (
      <div className="space-y-6">
        {/* 6 KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Spend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(kpis.total_spend || 0)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatCurrency(kpis.total_revenue || 0)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">ROAS</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${roasColor(kpis.roas || 0)}`}>
                {(kpis.roas || 0).toFixed(2)}x
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Profit</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(kpis.profit || 0)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Hit Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{(kpis.hit_rate_pct || 0).toFixed(1)}%</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Active UCODEs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis.active_ucodes_count || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Editors by ROAS */}
          {editorRankings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Top Editors by ROAS</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={editorRankings.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="editor_name" angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="roas" fill="#60a5fa" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Budget Share */}
          {budgetShare.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Budget Share by Editor</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={budgetShare} dataKey="spend" nameKey="editor_name" cx="50%" cy="50%" outerRadius={100} label>
                      {budgetShare.map((_: any, index: number) => (
                        <Cell key={index} fill={BUDGET_COLORS[index % BUDGET_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  const renderEditorRankingsTab = () => {
    const editorRankings = performanceData?.editor_rankings || [];

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profitability Table */}
        <Card>
          <CardHeader>
            <CardTitle>Editor Profitability</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left">#</th>
                    <th className="px-4 py-2 text-left">Editor</th>
                    <th className="px-4 py-2 text-right">UCODEs</th>
                    <th className="px-4 py-2 text-right">Spend</th>
                    <th className="px-4 py-2 text-right">Revenue</th>
                    <th className="px-4 py-2 text-right">ROAS</th>
                    <th className="px-4 py-2 text-right">Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {editorRankings
                    .sort((a: any, b: any) => (b.profit || 0) - (a.profit || 0))
                    .map((editor: any, idx: number) => (
                      <tr key={idx} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-4 py-2">{idx + 1}</td>
                        <td className="px-4 py-2 font-medium">{editor.editor_name}</td>
                        <td className="px-4 py-2 text-right">{editor.ucode_count || 0}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(editor.spend || 0)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(editor.revenue || 0)}</td>
                        <td className={`px-4 py-2 text-right font-semibold ${roasColor(editor.roas || 0)}`}>
                          {(editor.roas || 0).toFixed(2)}x
                        </td>
                        <td className="px-4 py-2 text-right text-green-600 dark:text-green-400">{formatCurrency(editor.profit || 0)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Quality Table */}
        <Card>
          <CardHeader>
            <CardTitle>Editor Quality Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left">#</th>
                    <th className="px-4 py-2 text-left">Editor</th>
                    <th className="px-4 py-2 text-left">Score</th>
                    <th className="px-4 py-2 text-right">Hook%</th>
                    <th className="px-4 py-2 text-right">Hold%</th>
                    <th className="px-4 py-2 text-right">CTR%</th>
                    <th className="px-4 py-2 text-right">Hits</th>
                  </tr>
                </thead>
                <tbody>
                  {editorRankings
                    .sort((a: any, b: any) => (b.quality_score || 0) - (a.quality_score || 0))
                    .map((editor: any, idx: number) => (
                      <tr key={idx} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-4 py-2">{idx + 1}</td>
                        <td className="px-4 py-2 font-medium">{editor.editor_name}</td>
                        <td className="px-4 py-2">
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-xs">
                            <div
                              className={`h-2 rounded-full ${
                                (editor.quality_score || 0) >= 75 ? 'bg-green-500' :
                                (editor.quality_score || 0) >= 50 ? 'bg-yellow-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(editor.quality_score || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{(editor.quality_score || 0).toFixed(0)}</span>
                        </td>
                        <td className="px-4 py-2 text-right">{(editor.avg_hook_rate || 0).toFixed(1)}%</td>
                        <td className="px-4 py-2 text-right">{(editor.avg_hold_rate || 0).toFixed(1)}%</td>
                        <td className="px-4 py-2 text-right">{(editor.avg_ctr || 0).toFixed(2)}%</td>
                        <td className="px-4 py-2 text-right font-semibold text-purple-600 dark:text-purple-400">{editor.hit_count || 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderCreativesTab = () => {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Creative Performance (Three-Level Expandable)</CardTitle>
          <CardDescription>Click UCODEs to expand and see ad accounts and individual ads</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left w-8"></th>
                  <th className="px-4 py-2 text-left">UCODE</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Editor</th>
                  <th className="px-4 py-2 text-left">Script</th>
                  <th className="px-4 py-2 text-right">Spend</th>
                  <th className="px-4 py-2 text-right">Revenue</th>
                  <th className="px-4 py-2 text-right">ROAS</th>
                  <th className="px-4 py-2 text-right">Hook%</th>
                  <th className="px-4 py-2 text-right">Hold%</th>
                </tr>
              </thead>
              <tbody>
                {filteredUcodes.map((ucode) => {
                  const isExpanded = expandedUcodes.has(ucode.ucode);
                  const detail = ucodeDetails.get(ucode.ucode);
                  const adBreakdown = detail?.ad_breakdown || [];
                  const groupedByAccount = adBreakdown.reduce((acc: any, ad: any) => {
                    const accountId = ad.ad_account_id || 'unknown';
                    if (!acc[accountId]) {
                      acc[accountId] = { accountId, ads: [], spend: 0, revenue: 0 };
                    }
                    acc[accountId].ads.push(ad);
                    acc[accountId].spend += ad.spend || 0;
                    acc[accountId].revenue += ad.revenue || 0;
                    return acc;
                  }, {});

                  return (
                    <React.Fragment key={ucode.ucode}>
                      {/* Level 1: UCODE */}
                      <tr className={`border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 ${ucode.is_unidentified ? 'opacity-75' : ''}`}>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => toggleUcodeExpand(ucode.ucode)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                            title={loadingDetail === ucode.ucode ? 'Loading...' : 'Expand'}
                          >
                            {loadingDetail === ucode.ucode ? (
                              <RefreshCw size={16} className="animate-spin" />
                            ) : isExpanded ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-2 font-mono text-blue-600 dark:text-blue-400 font-medium">
                          {ucode.ucode}
                          {ucode.is_unidentified && <span className="ml-1 text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded">Unid</span>}
                        </td>
                        <td className="px-4 py-2">{ucode.creative_type}</td>
                        <td className="px-4 py-2">{ucode.editor_name}</td>
                        <td className="px-4 py-2">{ucode.script_number || '—'}</td>
                        <td className="px-4 py-2 text-right">{formatMetric(ucode.spend, 'currency')}</td>
                        <td className="px-4 py-2 text-right">{formatMetric(ucode.revenue, 'currency')}</td>
                        <td className={`px-4 py-2 text-right font-semibold ${roasColor(ucode.roas || 0)}`}>
                          {(ucode.roas || 0).toFixed(2)}x
                        </td>
                        <td className="px-4 py-2 text-right">{formatMetric(ucode.hook_rate, 'percent')}</td>
                        <td className="px-4 py-2 text-right">{formatMetric(ucode.hold_rate, 'percent')}</td>
                      </tr>

                      {/* Level 2 & 3: Ad Accounts and Ads */}
                      {isExpanded && detail && (
                        <>
                          {Object.values(groupedByAccount).map((account: any) => {
                            const isAccountExpanded = expandedAccounts.has(account.accountId);
                            const roas = account.spend > 0 ? account.revenue / account.spend : 0;

                            return (
                              <React.Fragment key={`${ucode.ucode}-${account.accountId}`}>
                                {/* Level 2: Ad Account */}
                                <tr className="bg-blue-50 dark:bg-blue-900/20 border-b dark:border-gray-700 hover:bg-blue-100 dark:hover:bg-blue-900/40">
                                  <td className="px-4 py-2 pl-12">
                                    <button
                                      onClick={() => toggleAccountExpand(account.accountId)}
                                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    >
                                      {isAccountExpanded ? (
                                        <ChevronDown size={16} />
                                      ) : (
                                        <ChevronRight size={16} />
                                      )}
                                    </button>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                                    Account: {account.accountId}
                                  </td>
                                  <td colSpan={3} />
                                  <td className="px-4 py-2 text-right text-sm font-medium">{formatCurrency(account.spend)}</td>
                                  <td className="px-4 py-2 text-right text-sm font-medium">{formatCurrency(account.revenue)}</td>
                                  <td className={`px-4 py-2 text-right font-semibold text-sm ${roasColor(roas)}`}>
                                    {roas.toFixed(2)}x
                                  </td>
                                  <td colSpan={2} />
                                </tr>

                                {/* Level 3: Individual Ads */}
                                {isAccountExpanded && account.ads.map((ad: any, adIdx: number) => {
                                  const adRoas = ad.spend > 0 ? ad.revenue / ad.spend : 0;
                                  return (
                                    <tr key={`${ucode.ucode}-${account.accountId}-${adIdx}`} className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800">
                                      <td colSpan={2} className="px-4 py-2 pl-32 text-sm text-gray-600 dark:text-gray-400">
                                        {ad.ad_name?.substring(0, 50)}...
                                      </td>
                                      <td colSpan={3} />
                                      <td className="px-4 py-2 text-right text-sm">{formatCurrency(ad.spend)}</td>
                                      <td className="px-4 py-2 text-right text-sm">{formatCurrency(ad.revenue)}</td>
                                      <td className={`px-4 py-2 text-right font-semibold text-sm ${roasColor(adRoas)}`}>
                                        {adRoas.toFixed(2)}x
                                      </td>
                                      <td colSpan={2} />
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderHitRateTab = () => {
    const scatterData = ucodes.map(u => ({
      hook_rate: u.hook_rate || 0,
      hold_rate: u.hold_rate || 0,
      ucode: u.ucode,
      spend: u.spend || 0,
      isHit: (u.hook_rate || 0) > 45 && (u.hold_rate || 0) > 30
    }));

    const avgHookRate = hitUcodes.length > 0
      ? hitUcodes.reduce((sum, u) => sum + (u.hook_rate || 0), 0) / hitUcodes.length
      : 0;

    const avgHitSpend = hitUcodes.length > 0
      ? hitUcodes.reduce((sum, u) => sum + (u.spend || 0), 0) / hitUcodes.length
      : 0;

    const overallHitRate = ucodes.length > 0
      ? (hitUcodes.length / ucodes.length) * 100
      : 0;

    return (
      <div className="space-y-6">
        {/* KPI Strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Hits</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{hitUcodes.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Hit Rate %</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overallHitRate.toFixed(1)}%</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Hit Hook Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{avgHookRate.toFixed(1)}%</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Hit Spend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(avgHitSpend)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Scatter Chart */}
        {scatterData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Hook Rate vs Hold Rate (Hit Definition: Hook &gt; 45%, Hold &gt; 30%)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hook_rate" name="Hook Rate %" unit="%" />
                  <YAxis dataKey="hold_rate" name="Hold Rate %" unit="%" />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Scatter name="Hits" data={scatterData.filter(d => d.isHit)} fill="#10b981" />
                  <Scatter name="Other" data={scatterData.filter(d => !d.isHit)} fill="#d1d5db" />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Hit Creatives Table */}
        {hitUcodes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Hit Creatives ({hitUcodes.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-left">UCODE</th>
                      <th className="px-4 py-2 text-left">Editor</th>
                      <th className="px-4 py-2 text-right">Spend</th>
                      <th className="px-4 py-2 text-right">Revenue</th>
                      <th className="px-4 py-2 text-right">Hook%</th>
                      <th className="px-4 py-2 text-right">Hold%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hitUcodes.map((ucode) => (
                      <tr key={ucode.ucode} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-4 py-2 font-mono text-green-600 dark:text-green-400">{ucode.ucode}</td>
                        <td className="px-4 py-2">{ucode.editor_name}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(ucode.spend || 0)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(ucode.revenue || 0)}</td>
                        <td className="px-4 py-2 text-right font-semibold">{(ucode.hook_rate || 0).toFixed(1)}%</td>
                        <td className="px-4 py-2 text-right font-semibold">{(ucode.hold_rate || 0).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Near-Miss Creatives */}
        {nearMissUcodes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Near-Miss Creatives ({nearMissUcodes.length})</CardTitle>
              <CardDescription>Hook &gt; 38%, Hold &gt; 25%, Spend &gt; $30</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-left">UCODE</th>
                      <th className="px-4 py-2 text-left">Editor</th>
                      <th className="px-4 py-2 text-right">Spend</th>
                      <th className="px-4 py-2 text-right">Revenue</th>
                      <th className="px-4 py-2 text-right">Hook%</th>
                      <th className="px-4 py-2 text-right">Hold%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nearMissUcodes.map((ucode) => (
                      <tr key={ucode.ucode} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-4 py-2 font-mono text-yellow-600 dark:text-yellow-400">{ucode.ucode}</td>
                        <td className="px-4 py-2">{ucode.editor_name}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(ucode.spend || 0)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(ucode.revenue || 0)}</td>
                        <td className="px-4 py-2 text-right font-semibold">{(ucode.hook_rate || 0).toFixed(1)}%</td>
                        <td className="px-4 py-2 text-right font-semibold">{(ucode.hold_rate || 0).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderAllDataTab = () => {
    return (
      <Card>
        <CardHeader>
          <CardTitle>All Creative Data</CardTitle>
          <CardDescription>Search, filter, and sort all UCODEs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search UCODE or Editor</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <Search size={18} className="absolute right-3 top-2.5 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Creative Type</label>
              <select
                value={creativeTypeFilter}
                onChange={(e) => setCreativeTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="UGC">UGC</option>
                <option value="STATIC">Static</option>
                <option value="MOTION">Motion</option>
              </select>
            </div>
          </div>

          {/* Data Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setSortColumn('ucode'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>UCODE</th>
                  <th className="px-4 py-2 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setSortColumn('creative_type'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>Type</th>
                  <th className="px-4 py-2 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setSortColumn('editor_name'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>Editor</th>
                  <th className="px-4 py-2 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setSortColumn('vertical'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>Vertical</th>
                  <th className="px-4 py-2 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setSortColumn('script_number'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>Script</th>
                  <th className="px-4 py-2 text-right cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setSortColumn('spend'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>Spend</th>
                  <th className="px-4 py-2 text-right cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setSortColumn('revenue'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>Revenue</th>
                  <th className="px-4 py-2 text-right cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setSortColumn('roas'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>ROAS</th>
                  <th className="px-4 py-2 text-right cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setSortColumn('hook_rate'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>Hook%</th>
                  <th className="px-4 py-2 text-right cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setSortColumn('hold_rate'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); }}>Hold%</th>
                  <th className="px-4 py-2 text-right">CTR%</th>
                  <th className="px-4 py-2 text-right">Ads</th>
                  <th className="px-4 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedUcodes.map((ucode) => (
                  <tr key={ucode.ucode} className={`border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 ${ucode.is_unidentified ? 'opacity-75' : ''}`}>
                    <td className="px-4 py-2 font-mono text-blue-600 dark:text-blue-400 font-medium">
                      {ucode.ucode}
                      {ucode.is_unidentified && <span className="ml-1 text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded">Unidentified</span>}
                    </td>
                    <td className="px-4 py-2 text-xs">{ucode.creative_type}</td>
                    <td className="px-4 py-2 text-sm">{ucode.editor_name}</td>
                    <td className="px-4 py-2 text-sm">{ucode.vertical}</td>
                    <td className="px-4 py-2 text-sm">{ucode.script_number || '—'}</td>
                    <td className="px-4 py-2 text-right">{formatMetric(ucode.spend, 'currency')}</td>
                    <td className="px-4 py-2 text-right">{formatMetric(ucode.revenue, 'currency')}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${roasColor(ucode.roas || 0)}`}>
                      {(ucode.roas || 0).toFixed(2)}x
                    </td>
                    <td className="px-4 py-2 text-right">{formatMetric(ucode.hook_rate, 'percent')}</td>
                    <td className="px-4 py-2 text-right">{formatMetric(ucode.hold_rate, 'percent')}</td>
                    <td className="px-4 py-2 text-right">{formatMetric(ucode.ctr, 'percent')}</td>
                    <td className="px-4 py-2 text-right">{ucode.matched_ads_count || 0}</td>
                    <td className="px-4 py-2">
                      {ucode.age_tag && (
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getAgeTagColor(ucode.age_tag)}`}>
                          {ucode.age_tag === 'in_learning' && 'Learning'}
                          {ucode.age_tag === 'active' && 'Active'}
                          {ucode.age_tag === 'mature' && 'Mature'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout title="Analytics">
      <div className="space-y-6">
        {/* Facebook Sync Section (Collapsible) */}
        <details open={showFacebookSection} onToggle={(e) => setShowFacebookSection(e.currentTarget.open)}>
          <summary className="cursor-pointer select-none bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30">
            <span className="font-semibold flex items-center gap-2">
              <Facebook size={18} />
              Facebook Ads Sync
            </span>
          </summary>
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Date</label>
                <input
                  type="date"
                  value={syncDateFrom}
                  onChange={(e) => setSyncDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To Date</label>
                <input
                  type="date"
                  value={syncDateTo}
                  onChange={(e) => setSyncDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                />
              </div>
            </div>
            <Button onClick={syncAdMappings} disabled={syncing}>
              {syncing ? 'Syncing...' : 'Sync Ad Mappings'}
            </Button>
          </div>
        </details>

        {/* Filter Bar */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Date</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To Date</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vertical</label>
                <select
                  value={selectedVertical}
                  onChange={(e) => setSelectedVertical(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                >
                  <option value="">All Verticals</option>
                  {verticals.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button onClick={handleApplyFilters} disabled={filterLoading} className="w-full">
                  {filterLoading ? 'Loading...' : 'Apply'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div>
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
            <div className="flex justify-between items-center">
              <div className="flex gap-1 overflow-x-auto">
                {(['overview', 'editor-rankings', 'hit-rate', 'all-data', 'campaign-intelligence'] as TabType[]).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-6 py-3 font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab
                        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {tab === 'overview' && 'Overview'}
                    {tab === 'editor-rankings' && 'Editor Rankings'}
                    {tab === 'creatives' && 'Creatives'}
                    {tab === 'hit-rate' && 'Hit Rate'}
                    {tab === 'all-data' && 'All Data'}
                    {tab === 'campaign-intelligence' && '⚡ Campaign Intelligence'}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowMetadataImportModal(true)}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium flex items-center gap-2 whitespace-nowrap"
                >
                  📊 Import Metadata
                </button>
                <button
                  onClick={syncRedTrackData}
                  disabled={syncing}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2 whitespace-nowrap"
                >
                  {syncing ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      🔄 Sync RedTrack Data
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <RefreshCw className="animate-spin mr-2" />
              <span>Loading analytics data...</span>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && renderOverviewTab()}
              {activeTab === 'editor-rankings' && renderEditorRankingsTab()}
              {activeTab === 'creatives' && renderCreativesTab()}
              {activeTab === 'hit-rate' && renderHitRateTab()}
              {activeTab === 'all-data' && renderAllDataTab()}
              {activeTab === 'campaign-intelligence' && <CampaignIntelligenceTab />}
            </>
          )}
        </div>
      </div>

      {/* Metadata Import Modal */}
      {showMetadataImportModal && (
        <MetadataImportModal
          isOpen={showMetadataImportModal}
          onClose={() => setShowMetadataImportModal(false)}
          onSuccess={() => {
            fetchData();
            setShowMetadataImportModal(false);
          }}
        />
      )}
    </DashboardLayout>
  );
}
